import React, { useState, useEffect, useRef } from 'react'
import { useFormContext, useController } from 'react-hook-form'
import { TextField as MuiTextField } from '@mui/material'
import debounce from 'lodash/debounce'
import IsinsApi from '../../api/IsinsApi'

const IsinField = ({ label, name, formMethods, controllerFields, onChangeValue, initialValue, ...props }) => {
    const [inputValue, setInputValue] = useState(initialValue || '')

    useEffect(() => {
        setInputValue(initialValue || '')
    }, [initialValue])

    const cleanInputValue = (v) => {
        v = v?.trim() || ''
        const validChars = /[A-Za-z0-9]/
        const cleanValue = v
            .split('')
            .filter((c) => c.match(validChars))
            .join('')
        return cleanValue
    }

    const handleInputChange = async (event) => {
        const v = cleanInputValue(event.target.value)
        if (onChangeValue) onChangeValue(v)
        setInputValue(v)
        controllerFields.field.onChange(v)

        // Trigger validation
        await formMethods.trigger(name)
    }

    return (
        <MuiTextField
            label={label}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={controllerFields.field.onBlur}
            name={controllerFields.field.name}
            inputRef={controllerFields.field.ref}
            error={!!controllerFields.fieldState.error}
            helperText={controllerFields.fieldState.error ? controllerFields.fieldState.error.message : ''}
            {...props}
        />
    )
}

const fetchIsIsinUsed = (entityId) => {
    const isinsApi = new IsinsApi()

    const fetchFunction = async (isin) => {
        if (!isin) return false
        if (isin.length !== 12) return false
        const isinCount = await isinsApi.fetchIsinCount(isin, entityId)
        return isinCount.isins.total > 0
    }
    return fetchFunction
}

// Create a debounced function that correctly returns a promise
const createDebouncedCheck = (func, wait) => {
    let currentPromise = null
    const debouncedFunc = debounce((resolve, reject, ...args) => {
        func(...args)
            .then(resolve)
            .catch(reject)
    }, wait)

    return (...args) => {
        if (currentPromise) {
            currentPromise.cancel()
        }

        let cancel
        const promise = new Promise((resolve, reject) => {
            cancel = () => reject({ isCanceled: true })
            debouncedFunc(resolve, reject, ...args)
        })
        promise.cancel = cancel
        currentPromise = promise
        return promise
    }
}

const RegisteredIsinField = (props) => {
    const formMethods = useFormContext()
    const initialFormValueRef = useRef(formMethods.getValues(props.name))
    const initialValue = initialFormValueRef.current
    let { issueId, warrantId, entityId, ...restOfProps } = props
    entityId = entityId || issueId || warrantId
    const debouncedFetchIsIsinUsed = createDebouncedCheck(fetchIsIsinUsed(entityId), 400)

    const { field, fieldState, formState } = useController({
        name: props.name,
        control: formMethods.control,
        rules: {
            minLength: { value: 12, message: 'ISIN ska vara 12 tecken' },
            maxLength: { value: 12, message: 'ISIN ska vara 12 tecken' },
            validate: {
                async isUnique(value) {
                    // Skip validation if the value is the same as the initial value
                    if (value === initialValue) {
                        return true
                    }

                    const isUsed = await debouncedFetchIsIsinUsed(value)
                    return isUsed ? 'ISIN redan använt' : true
                },
            },
        },
    })

    const forwardProps = {
        controllerFields: { field, fieldState, formState },
        formMethods,
        initialValue,
        ...restOfProps,
    }
    return <IsinField {...forwardProps} />
}

export { RegisteredIsinField }
